import { AnimatePresence, motion } from "framer-motion";
import _ from "lodash";
import { useMemo, useState, type CSSProperties } from "react";
import {
  CircleDotIcon,
  LibraryIcon,
  TriangleIcon,
  type LucideIcon,
} from "lucide-react";

import { Values } from "@/constants";

/** Leaf rows use a shorter bar than group headers (same horizontal layout). */
const LEAF_ROW_HEIGHT = Math.round(Values.DEFAULT_ROW_HEIGHT * 0.76);
const GROUP_ROW_HEIGHT = Math.round(Values.DEFAULT_ROW_HEIGHT * 0.85);
/** How far the grand total sits off the top of the card. */
const TOTAL_ROW_TOP_PADDING = 8;
/** The grand total at the top, which every branch sticks below. */
const TOTAL_ROW_HEIGHT = GROUP_ROW_HEIGHT + TOTAL_ROW_TOP_PADDING;
import { useEnumColor } from "@/hooks/useEnumColor";
import { EnumColors } from "@/constants/values";
import { Icon } from "@/components/atoms/Icon";
import { icons } from "@/objects/@icons";
import { State } from "@/state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

// Types for the component
type AggregationOperation = "mean" | "sum" | "max" | "min" | "median";

/** Spacing between nested grouping cards only. Default `md`. */
type ReportsViewGroupedSummaryWidgetSize = "sm" | "md" | "lg";

/** Inset for embedded mode — applied inside the scroll area so the scrollbar stays flush. */
export const GROUPED_SUMMARY_EMBEDDED_PADDING = {
  px: 2,
  py: 1.5,
} as const;

const NESTED_GROUP_SPACING: Record<
  ReportsViewGroupedSummaryWidgetSize,
  { x: number; y: number }
> = {
  sm: { x: 5, y: 1.25 },
  md: { x: 10, y: 2 },
  lg: { x: 16, y: 3.5 },
};

function getNestedGroupSpacing(
  size: ReportsViewGroupedSummaryWidgetSize = "md",
): { gapX: number; gapY: number } {
  const { x, y } = NESTED_GROUP_SPACING[size];
  return { gapX: x, gapY: y };
}

interface ReportsViewGroupedSummaryWidgetProps<T> {
  data: T[];
  type: string;
  numericKeys: Array<
    { [K in keyof T]: T[K] extends number ? K : never }[keyof T]
  >;
  operation: AggregationOperation;
  groupingKeys: Array<keyof T>; // Required now - defines the hierarchy
  isLoading?: boolean;
  showDeltas?: boolean;
  deltaData?: T[];
  prefix?: string;
  suffix?: string;
  formatValue?: (value: number) => string;
  /** Inset between nested grouping cards (`sm` / `md` / `lg`). Outer card padding matches `ReportsViewCard.tsx`. */
  size?: ReportsViewGroupedSummaryWidgetSize;
  /** When true, omit inner card chrome — content fills a parent `ReportsViewCard`. */
  embedded?: boolean;
  /**
   * Node id for the record a group row stands for, given the grouping key and
   * one of its rows. A row with an id renders its label as a breadcrumb link
   * that opens that record in the node panel; without one it stays plain text.
   */
  getGroupNodeId?: (groupKey: string, row: T) => string | null;
  /**
   * The icon a row carries, given the key it is grouped by and one of its
   * rows - so each level of a hierarchy can show what it is of. Given
   * nothing, a branch shows that it holds rows and a leaf shows that it is one.
   */
  rowIcon?: (groupKey: string, row: T) => LucideIcon | null;
  /**
   * Whether a branch shows a figure of its own. A branch totals what is under
   * it, which is only true of figures that add up: the sum of the averages
   * beneath a heading is a number that is true of nothing.
   */
  hideGroupTotals?: boolean;
}

// Utility functions for aggregation operations
const aggregationFunctions = {
  sum: (values: number[]): number => values.reduce((acc, val) => acc + val, 0),
  mean: (values: number[]): number =>
    values.length > 0
      ? values.reduce((acc, val) => acc + val, 0) / values.length
      : 0,
  max: (values: number[]): number =>
    values.length > 0 ? Math.max(...values) : 0,
  min: (values: number[]): number =>
    values.length > 0 ? Math.min(...values) : 0,
  median: (values: number[]): number => {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
      ? (sorted[mid - 1] + sorted[mid]) / 2
      : sorted[mid];
  },
};

// Helper function to extract numeric values from objects
function extractNumericValues<T>(
  data: T[],
  keys: Array<{ [K in keyof T]: T[K] extends number ? K : never }[keyof T]>,
): Record<string, number[]> {
  const result: Record<string, number[]> = {};

  keys.forEach((key) => {
    result[String(key)] = data
      .map((item) => item[key] as number)
      .filter(
        (value): value is number => typeof value === "number" && !isNaN(value),
      );
  });

  return result;
}

// Helper function to calculate aggregated values
function calculateAggregatedValues<T>(
  data: T[],
  numericKeys: Array<
    { [K in keyof T]: T[K] extends number ? K : never }[keyof T]
  >,
  operation: AggregationOperation,
): Record<string, number> {
  const numericValues = extractNumericValues(data, numericKeys);
  const aggregatedValues: Record<string, number> = {};

  Object.entries(numericValues).forEach(([key, values]) => {
    aggregatedValues[key] = aggregationFunctions[operation](values);
  });

  return aggregatedValues;
}

// Type for hierarchical group structure
interface GroupNode<T> {
  label: string;
  groupKey: string; // The grouping key name (e.g., 'category', 'type')
  groupValue: string; // The value for this group (e.g., 'Revenue', 'Inflow')
  data: T[]; // Raw data items in this group
  aggregatedValues: Record<string, number>;
  deltas?: Record<string, number>;
  depth: number;
  children: GroupNode<T>[]; // Sub-groups
  isLeaf: boolean; // True if this is the final level (no more grouping)
}

// Build hierarchical group structure
function buildGroupHierarchy<T>(
  data: T[],
  groupingKeys: Array<keyof T>,
  numericKeys: Array<
    { [K in keyof T]: T[K] extends number ? K : never }[keyof T]
  >,
  operation: AggregationOperation,
  depth: number = 0,
): GroupNode<T>[] {
  if (groupingKeys.length === 0 || depth >= groupingKeys.length) {
    return [];
  }

  const currentKey = groupingKeys[depth];
  const isLastKey = depth === groupingKeys.length - 1;

  if (isLastKey) {
    // For the last grouping key, create leaf nodes based on the primary numeric column values
    const primaryNumericKey = numericKeys[0];

    return data.map((item, index) => {
      const leafValue = item[primaryNumericKey] as number;
      const leafLabel = item[currentKey]
        ? String(item[currentKey])
        : `Item ${index + 1}`;

      // For leaf nodes, the "aggregated" values are just the individual item values
      const leafAggregatedValues = calculateAggregatedValues(
        [item],
        numericKeys,
        operation,
      );

      return {
        label: leafLabel,
        groupKey: String(currentKey),
        groupValue: leafLabel,
        data: [item],
        aggregatedValues: leafAggregatedValues,
        depth,
        children: [],
        isLeaf: true,
      };
    });
  }

  // Group items by current key for non-leaf levels
  const groups = data.reduce(
    (acc, item) => {
      const groupValue = String(item[currentKey]);
      if (!acc[groupValue]) {
        acc[groupValue] = [];
      }
      acc[groupValue].push(item);
      return acc;
    },
    {} as Record<string, T[]>,
  );

  // Build group nodes
  const groupNodes = Object.entries(groups).map(
    ([groupValue, groupItems], index) => {
      const aggregatedValues = calculateAggregatedValues(
        groupItems,
        numericKeys,
        operation,
      );

      const children = buildGroupHierarchy(
        groupItems,
        groupingKeys,
        numericKeys,
        operation,
        depth + 1,
      );

      return {
        label: groupValue,
        groupKey: String(currentKey),
        groupValue,
        data: groupItems,
        aggregatedValues,
        depth,
        children,
        isLeaf: false,
      };
    },
  );

  // Filter out groups with no data
  return filterGroupsWithData(groupNodes);
}

// Recursively check if a group has any data down to leaf nodes
function hasDataInGroup<T>(group: GroupNode<T>): boolean {
  // If this is a leaf node, check if it has actual data
  if (group.isLeaf) {
    return group.data.length > 0;
  }

  // If not a leaf, check if any children have data
  return group.children.some((child) => hasDataInGroup(child));
}

// Filter out groups that have no data all the way down to their leaves
function filterGroupsWithData<T>(groups: GroupNode<T>[]): GroupNode<T>[] {
  return groups
    .filter((group) => hasDataInGroup(group))
    .map((group) => ({
      ...group,
      children: filterGroupsWithData(group.children),
    }));
}

// Calculate the maximum depth in the hierarchy
function calculateMaxDepth<T>(groups: GroupNode<T>[]): number {
  if (groups.length === 0) return 0;

  return Math.max(
    ...groups.map((group) => {
      if (group.children.length === 0) {
        return group.depth;
      }
      return Math.max(group.depth, calculateMaxDepth(group.children));
    }),
  );
}

// Component for rendering individual cells
function ReportsViewMetricCell({
  value,
  isLoading,
  prefix = "",
  suffix = "",
  formatValue,
  delta,
  isLeaf,
}: {
  value: number;
  isLoading: boolean;
  prefix?: string;
  suffix?: string;
  formatValue?: (value: number) => string;
  delta?: number;
  isLeaf: boolean;
}) {
  const isIncreasing = delta != null && delta > 0;

  if (isLoading) {
    return <Skeleton variant="text" level="body-sm" width={72} />;
  }

  const displayValue = formatValue
    ? formatValue(value)
    : `${prefix}${value.toLocaleString()}${suffix}`;

  return (
    <div className="flex items-center gap-2">
      <Typography
        level="body-sm"
        className={cn(
          isLeaf ? "text-foreground" : "text-muted-foreground/80",
          isLeaf && "[line-height:1.3]",
        )}
      >
        {displayValue}
      </Typography>
      {delta != null && (
        <Typography
          level="body-sm"
          className={cn(
            "flex items-center gap-1.5",
            "pl-1 pr-1 pt-0.5 pb-0.5 rounded text-[11px]",
            isIncreasing ? "text-[success.main]" : "text-[danger.main]",
          )}
        >
          <TriangleIcon
            // Filled: a mark, not a drawing.
            fill="currentColor"
            size={6}
            style={{
              transform:
                isIncreasing === null
                  ? "rotate(90deg)"
                  : isIncreasing
                    ? "rotate(0deg)"
                    : "rotate(180deg)",
              transition: "all 0.2s",
            }}
          />
          {Math.abs(delta).toFixed(1)}%
        </Typography>
      )}
    </div>
  );
}

// Component for rendering individual group with border
function ReportsViewGroupWithBorder<T>({
  group,
  numericKeys,
  isLoading,
  showDeltas,
  prefix,
  suffix,
  formatValue,
  maxDepth,
  size = "md",
  getGroupNodeId,
  rowIcon,
  hideGroupTotals = false,
  surface,
  stickyTop,
}: {
  group: GroupNode<T>;
  numericKeys: Array<
    { [K in keyof T]: T[K] extends number ? K : never }[keyof T]
  >;
  isLoading: boolean;
  showDeltas: boolean;
  prefix: string;
  suffix: string;
  formatValue?: (value: number) => string;
  maxDepth: number;
  size?: ReportsViewGroupedSummaryWidgetSize;
  getGroupNodeId?: (groupKey: string, row: T) => string | null;
  rowIcon?: (groupKey: string, row: T) => LucideIcon | null;
  hideGroupTotals?: boolean;
  /** What a sticky branch paints over the rows passing under it. */
  surface: string;
  /** Where the total sticks: below the scroll area's own top inset. */
  stickyTop: number;
}) {
  const { gapX, gapY } = getNestedGroupSpacing(size);
  // State for collapsed/expanded groups (only for non-leaf groups)
  const [isCollapsed, setIsCollapsed] = useState(false);
  /** Whether the rows under this group are still growing into place. */
  const [isOpening, setIsOpening] = useState(false);
  const openNodePanel = State((state) => state.openNodePanel);
  const nodeId = getGroupNodeId?.(group.groupKey, group.data[0]) ?? null;

  return (
    <div
    // Nothing clips here. A group used to hide its overflow, which made it a
    // scroll area of its own - and a sticky header measures its offset
    // against the nearest one, so every nested branch was pushed a row down
    // inside its own box, leaving a blank band above it and sitting on top
    // of the first row under it. The rows stick to the card that scrolls.
    >
      {/* Group Header Row */}
      <div
        className={cn(
          "flex items-center",
          group.isLeaf && "overflow-hidden",
          // No inset of its own: a row begins where its level begins, so what
          // it sits under is read off the indent alone.
          "pl-0 pr-0",
          // Depth reads from the indent; every row is separated by a hairline
          // rather than by a per-level tint. The line belongs to the row above
          // it, so a branch pinned to the top of the card keeps the line that
          // separates it from the rows sliding under it.
          "[border-bottom-width:1px] [border-bottom-style:solid] border-border",
        )}
        style={{
          height: group.isLeaf ? LEAF_ROW_HEIGHT : GROUP_ROW_HEIGHT,
          // A branch stays in view while the rows under it scroll past, so
          // there is always something saying what is being read. A nested one
          // sits below its parent rather than on top of it.
          ...(group.isLeaf
            ? {}
            : {
                position: "sticky",
                top:
                  stickyTop + TOTAL_ROW_HEIGHT + group.depth * GROUP_ROW_HEIGHT,
                zIndex: 2 + maxDepth - group.depth,
                backgroundColor: surface,
              }),
        }}
      >
        {/* Group Label */}
        <div
          onClick={
            !group.isLeaf ? () => setIsCollapsed(!isCollapsed) : undefined
          }
          className={cn(
            "flex-1 min-w-0 flex flex-row justify-start",
            group.isLeaf ? "items-center" : "items-baseline",
            "gap-2 pl-0 pr-0",
            !group.isLeaf ? "cursor-pointer" : "cursor-default",
            !group.isLeaf && "hover:bg-[action.hover]",
            "rounded-sm [transition:background-color_0.2s_ease]",
          )}
        >
          <div className="shrink-0 flex items-center text-muted-foreground/80">
            <Icon
              ReactIcon={
                rowIcon?.(group.groupKey, group.data[0]) ??
                (group.isLeaf ? CircleDotIcon : LibraryIcon)
              }
              size="sm"
            />
          </div>
          <Typography
            level="body-md"
            // Clicking the label opens the record; the rest of the row still
            // collapses the group, so the link has to keep the click.
            onClick={
              nodeId
                ? (event) => {
                    event.stopPropagation();
                    openNodePanel(nodeId, { autofocus: false });
                  }
                : undefined
            }
            className={cn(
              "truncate",
              "min-w-0",
              group.isLeaf ? "text-foreground" : "text-muted-foreground",
              group.isLeaf && "[line-height:1.3]",
              nodeId && "cursor-pointer hover:[text-decoration:underline]",
            )}
          >
            {group.label}
          </Typography>
          {/* Chevron for non-leaf groups */}
          {!group.isLeaf && (
            <Button
              variant="ghost"
              size="icon-xs"
              className="shrink-0 text-muted-foreground/80 transform-[translateY(4px)]"
            >
              <Icon
                ReactIcon={icons.chevronRight}
                style={{
                  transform: isCollapsed ? "rotate(0deg)" : "rotate(90deg)",
                  transition: "transform 0.2s ease",
                }}
              />
            </Button>
          )}
        </div>

        {/* Aggregated Values — a branch shows one only where it adds up. */}
        {(group.isLeaf || !hideGroupTotals) &&
          numericKeys.map((key, index) => (
            <div
              key={String(key)}
              className={cn(
                "shrink-0 pl-4 flex h-full justify-end pr-(--max-depth)",
                index !== numericKeys.length - 1 &&
                  "[box-shadow:inset_-1px_0_0_0_var(--border)]",
              )}
              style={{ "--max-depth": `${maxDepth * 8}px` } as CSSProperties}
            >
              <ReportsViewMetricCell
                value={group.aggregatedValues[String(key)] || 0}
                isLoading={isLoading}
                prefix={prefix}
                isLeaf={group.isLeaf}
                suffix={suffix}
                formatValue={formatValue}
                delta={
                  showDeltas && group.deltas
                    ? group.deltas[String(key)]
                    : undefined
                }
              />
            </div>
          ))}
      </div>

      {/* Children Groups */}
      <AnimatePresence>
        {group.children.length > 0 && !isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            onAnimationStart={() => setIsOpening(true)}
            onAnimationComplete={() => setIsOpening(false)}
            // Clipped only while the group is growing into place. Left
            // clipped, this is a scroll area of its own, and the branches
            // inside it stick to *it* rather than to the card that scrolls -
            // which pushes each one a row down into the rows it heads.
            style={{ overflow: isOpening ? "hidden" : undefined }}
          >
            <div
              // Left inset only, so every row — however deep — ends at the
              // same right edge and the amounts line up. Every level steps
              // in, the first one included: what a row sits under should be
              // readable from where it starts.
              className={cn(
                "pl-(--padding-left)",
                group.depth === 0 ? "pb-0" : "pb-(--padding-bottom)",
                "pt-0",
              )}
              style={
                {
                  "--padding-left": `${gapX * 0.5 * 8}px`,
                  "--padding-bottom": !(group.depth === 0)
                    ? `${gapY * 0.5 * 8}px`
                    : undefined,
                } as CSSProperties
              }
            >
              {group.children.map((child, index) => (
                <ReportsViewGroupWithBorder
                  key={`${child.groupKey}-${child.groupValue}-${index}`}
                  group={child}
                  numericKeys={numericKeys}
                  isLoading={isLoading}
                  showDeltas={showDeltas}
                  prefix={prefix}
                  suffix={suffix}
                  formatValue={formatValue}
                  maxDepth={maxDepth}
                  size={size}
                  getGroupNodeId={getGroupNodeId}
                  rowIcon={rowIcon}
                  hideGroupTotals={hideGroupTotals}
                  surface={surface}
                  stickyTop={stickyTop}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* No additional leaf rendering needed - leaves are now individual data items */}
    </div>
  );
}

// Main table component
function ReportsViewGroupedTable<T>({
  data,
  numericKeys,
  groupingKeys,
  operation,
  isLoading,
  showDeltas,
  deltaData,
  prefix,
  suffix,
  formatValue,
  size = "md",
  embedded = false,
  getGroupNodeId,
  rowIcon,
  hideGroupTotals = false,
}: ReportsViewGroupedSummaryWidgetProps<T>) {
  const { gapX } = getNestedGroupSpacing(size);
  const stickyHeaderBg = embedded ? "var(--report-card-surface)" : "white";
  // Embedded, the inset is inside the scroll area, so the total sticks where
  // it starts rather than sliding up through the inset first.
  const stickyTop = embedded ? GROUPED_SUMMARY_EMBEDDED_PADDING.py * 8 : 0;
  const groupHierarchy = useMemo(
    () => buildGroupHierarchy(data, groupingKeys, numericKeys, operation),
    [data, groupingKeys, numericKeys, operation],
  );

  const deltaHierarchy = useMemo(() => {
    if (!showDeltas || !deltaData) return [];
    return buildGroupHierarchy(deltaData, groupingKeys, numericKeys, operation);
  }, [deltaData, groupingKeys, numericKeys, operation, showDeltas]);

  // Add deltas to groups by matching group paths
  const addDeltasToGroups = (
    groups: GroupNode<T>[],
    deltaGroups: GroupNode<T>[],
  ): GroupNode<T>[] => {
    return groups.map((group) => {
      const matchingDelta = deltaGroups.find(
        (deltaGroup) =>
          deltaGroup.groupValue === group.groupValue &&
          deltaGroup.groupKey === group.groupKey,
      );

      let deltas: Record<string, number> | undefined;
      if (matchingDelta) {
        deltas = {};
        numericKeys.forEach((key) => {
          const currentValue = group.aggregatedValues[String(key)] || 0;
          const previousValue =
            matchingDelta.aggregatedValues[String(key)] || 0;
          if (previousValue !== 0) {
            deltas![String(key)] =
              ((currentValue - previousValue) / previousValue) * 100;
          }
        });
      }

      return {
        ...group,
        deltas,
        children: addDeltasToGroups(
          group.children,
          matchingDelta?.children || [],
        ),
      };
    });
  };

  const groupsWithDeltas = showDeltas
    ? addDeltasToGroups(groupHierarchy, deltaHierarchy)
    : groupHierarchy;

  // Calculate maximum depth for proper alignment
  const maxDepth = calculateMaxDepth(groupsWithDeltas);

  const enumColor = useEnumColor(EnumColors.GREEN);

  return (
    <div>
      {/* Grouping wide summary row */}
      <div
        // The rows carry their line underneath, so the total carries its own
        // rather than borrowing the first row's.
        // The total stays put, and every branch sticks below it - so what is
        // being read and what it all comes to are both always on screen.
        // The inset above it is still the scroll area's, so the rows passing
        // under it would show through there: it is covered over.
        // The branches stick below it, so what it measures has to be what
        // it takes up - padding and hairline included.
        className={cn(
          "flex justify-end pl-0 pr-0 items-baseline",
          embedded ? "bg-(--report-card-surface)" : "bg-white",
          "[border-bottom-width:1px] [border-bottom-style:solid] border-border sticky",
          embedded ? "top-3" : "top-0",
          "z-(--z-index)",
          embedded
            ? "[&::before]:[content:''] [&::before]:absolute [&::before]:left-0 [&::before]:right-0 [&::before]:bottom-full [&::before]:h-3 [&::before]:bg-(--report-card-surface)"
            : "[&::before]:[content:''] [&::before]:absolute [&::before]:left-0 [&::before]:right-0 [&::before]:bottom-full [&::before]:h-0 [&::before]:bg-white",
          "box-border h-(--height-3) pt-2",
        )}
        style={
          {
            "--z-index": String(3 + maxDepth),
            "--height-3":
              GROUP_ROW_HEIGHT + TOTAL_ROW_TOP_PADDING <= 1 &&
              GROUP_ROW_HEIGHT + TOTAL_ROW_TOP_PADDING !== 0
                ? `${(GROUP_ROW_HEIGHT + TOTAL_ROW_TOP_PADDING) * 100}%`
                : `${GROUP_ROW_HEIGHT + TOTAL_ROW_TOP_PADDING}px`,
          } as CSSProperties
        }
      >
        <Typography
          level="h3"
          // sx={{ px: 1, py: 0.5, border: 1, borderRadius: "sm", ...enumColor }}
        >
          {(() => {
            const firstKey = numericKeys[0];
            // What it all comes to only exists where the figures add up: a
            // total of averages is a number true of nothing.
            if (!firstKey || hideGroupTotals) return null;
            const grandTotal = groupsWithDeltas.reduce(
              (sum, group) =>
                sum + (group.aggregatedValues[String(firstKey)] || 0),
              0,
            );
            return formatValue
              ? formatValue(grandTotal)
              : `${prefix ?? ""}${grandTotal.toLocaleString()}${suffix ?? ""}`;
          })()}
        </Typography>
        <div className="grow" />
        {/* Numeric column headers */}
        {numericKeys.map((key, index) => (
          <div
            key={String(key)}
            className="shrink-0 pl-4 text-right flex flex-row items-baseline justify-end gap-2 pr-(--max-depth-2)"
            style={{ "--max-depth-2": `${maxDepth * 8}px` } as CSSProperties}
          >
            {index === 0 && !hideGroupTotals && (
              <Typography
                level="body-xs"
                className="text-muted-foreground/80 uppercase opacity-[0.5]"
              >
                {operation}
              </Typography>
            )}
            <Typography level="body-sm" className="text-muted-foreground/80">
              {(() => {
                if (hideGroupTotals) return null;
                // Calculate total aggregated value for this column across all groups
                const totalValue = groupsWithDeltas.reduce((sum, group) => {
                  return sum + (group.aggregatedValues[String(key)] || 0);
                }, 0);

                return formatValue
                  ? formatValue(totalValue)
                  : `${prefix}${totalValue.toLocaleString()}`;
              })()}
            </Typography>
          </div>
        ))}
      </div>

      {/* Hierarchical Groups — flush stack; horizontal inset from parent padding */}
      <div className="pl-0 pr-0 pb-0 pt-0">
        {groupsWithDeltas.map((group, index) => (
          <ReportsViewGroupWithBorder
            key={`${group.groupKey}-${group.groupValue}-${index}`}
            group={group}
            numericKeys={numericKeys}
            isLoading={isLoading}
            showDeltas={showDeltas}
            prefix={prefix}
            suffix={suffix}
            formatValue={formatValue}
            maxDepth={maxDepth}
            size={size}
            getGroupNodeId={getGroupNodeId}
            rowIcon={rowIcon}
            hideGroupTotals={hideGroupTotals}
            surface={stickyHeaderBg}
            stickyTop={stickyTop}
          />
        ))}
      </div>
    </div>
  );
}

// Main component
export function ReportsViewGroupedSummaryWidget<T>(
  props: ReportsViewGroupedSummaryWidgetProps<T>,
) {
  const {
    data,
    numericKeys,
    operation,
    type,
    groupingKeys,
    isLoading = false,
    showDeltas = false,
    deltaData,
    prefix = "$",
    suffix = "",
    formatValue,
    size = "md",
    embedded = false,
    getGroupNodeId,
    rowIcon,
    hideGroupTotals = false,
  } = props;

  if (!data || data.length === 0) {
    return (
      <div
        className={cn(
          embedded
            ? "w-full h-full flex flex-col overflow-hidden min-h-0"
            : "relative rounded-lg w-full h-full [border-width:1px] [border-style:solid] border-input shadow-sm bg-white flex flex-col overflow-hidden",
          embedded ? "pl-4 pr-4 pt-3 pb-3" : "p-4",
          "items-center justify-center",
        )}
      >
        <Typography level="body-sm" className="text-muted-foreground">
          No data available
        </Typography>
      </div>
    );
  }

  const dataWithType = data.map((item) => ({
    ...item,
    // __type: type,
  }));
  const groupingKeysWithType = [...groupingKeys] as Array<keyof T>;
  // const groupingKeysWithType = ["__type", ...groupingKeys] as Array<keyof T>;

  if (numericKeys.length === 0) {
    return (
      <div
        className={cn(
          embedded
            ? "w-full h-full flex flex-col overflow-hidden min-h-0"
            : "relative rounded-lg w-full h-full [border-width:1px] [border-style:solid] border-input shadow-sm bg-white flex flex-col overflow-hidden",
          embedded ? "pl-4 pr-4 pt-3 pb-3" : "p-4",
          "items-center justify-center",
        )}
      >
        <Typography level="body-sm" className="text-foreground">
          No numeric keys specified
        </Typography>
      </div>
    );
  }

  if (!groupingKeys || groupingKeys.length === 0) {
    return (
      <div
        className={cn(
          embedded
            ? "w-full h-full flex flex-col overflow-hidden min-h-0"
            : "relative rounded-lg w-full h-full [border-width:1px] [border-style:solid] border-input shadow-sm bg-white flex flex-col overflow-hidden",
          embedded ? "pl-4 pr-4 pt-3 pb-3" : "p-4",
          "items-center justify-center",
        )}
      >
        <Typography level="body-sm" className="text-foreground">
          No grouping keys specified
        </Typography>
      </div>
    );
  }

  return (
    <div
      className={cn(
        embedded
          ? "w-full h-full flex flex-col overflow-hidden min-h-0"
          : "relative rounded-lg w-full h-full [border-width:1px] [border-style:solid] border-input shadow-sm bg-white flex flex-col overflow-hidden",
        embedded ? "" : "p-4",
        "min-h-0",
      )}
    >
      {/* Vertical scroll only — labels ellipsize rather than push the amounts
          out of view, so nothing should ever scroll sideways. */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        <div className={cn(embedded && "pl-4 pr-4 pt-3 pb-3")}>
          <ReportsViewGroupedTable
            data={dataWithType}
            numericKeys={numericKeys}
            operation={operation}
            groupingKeys={groupingKeysWithType}
            isLoading={isLoading}
            showDeltas={showDeltas}
            deltaData={deltaData}
            prefix={prefix}
            suffix={suffix}
            formatValue={formatValue}
            type={type}
            size={size}
            embedded={embedded}
            getGroupNodeId={getGroupNodeId}
            rowIcon={rowIcon}
            hideGroupTotals={hideGroupTotals}
          />
        </div>
      </div>
    </div>
  );
}
